import { useEffect, useState } from 'react'

import {
  mergeCatalogDuplicates,
  type CatalogDuplicateGroupDto,
  type CatalogDuplicateMatchReason,
  type SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import { mergeMyPricesDuplicates } from '../api/restaurantMyPrices'
import { formatUnitLabel } from '../utils/formatUnitLabel'

const reasonLabel = (reason: CatalogDuplicateMatchReason): string => {
  switch (reason) {
    case 'EXTERNAL_SKU':
      return 'одинаковый артикул'
    case 'IMPORT_SOURCE_NAME':
      return 'одинаковое имя из импорта'
    case 'NAME_SIMILAR':
      return 'похожее наименование'
    default:
      return reason
  }
}

const formatPrice = (item: SupplierCatalogItemDto): string => {
  if (item.price == null) return '—'
  return String(item.price)
}

/** Дата загрузки/обновления позиции в прайсе (тот же updatedAt, что «Дата обновления» в таблице). */
const formatUploadedAt = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const itemMetaLine = (item: SupplierCatalogItemDto): string =>
  [
    item.externalSku ? `арт. ${item.externalSku}` : null,
    item.brand,
    formatPrice(item),
    item.unit ? formatUnitLabel(item.unit) : null,
  ]
    .filter(Boolean)
    .join(' · ')

type CatalogDuplicateMergePanelProps = {
  organizationId: number
  groups: CatalogDuplicateGroupDto[]
  catalogMode?: 'supplier' | 'restaurant-my-prices'
  onMerged: () => Promise<void> | void
  onDismiss: () => void
  onNotDuplicates: (group: CatalogDuplicateGroupDto) => void
}

export const CatalogDuplicateMergePanel = ({
  organizationId,
  groups,
  catalogMode = 'supplier',
  onMerged,
  onDismiss,
  onNotDuplicates,
}: CatalogDuplicateMergePanelProps) => {
  const [groupIndex, setGroupIndex] = useState(0)
  const [keepId, setKeepId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [price, setPrice] = useState('')
  const [externalSku, setExternalSku] = useState('')
  const [merging, setMerging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const safeIndex = Math.min(groupIndex, Math.max(0, groups.length - 1))
  const group = groups[safeIndex] ?? null
  const isMyPrices = catalogMode === 'restaurant-my-prices'

  useEffect(() => {
    if (groupIndex !== safeIndex) {
      setGroupIndex(safeIndex)
    }
  }, [groupIndex, safeIndex])

  useEffect(() => {
    if (!group || group.items.length === 0) {
      setKeepId(null)
      return
    }
    const preferred = group.items[0]
    setKeepId(preferred.id)
    setName(preferred.supplierName)
    setBrand(preferred.brand ?? '')
    setPrice(preferred.price != null ? String(preferred.price) : '')
    setExternalSku(preferred.externalSku ?? '')
    setError(null)
  }, [safeIndex, group])

  if (!group) {
    return null
  }

  const handleSelectKeep = (item: SupplierCatalogItemDto) => {
    setKeepId(item.id)
    setName(item.supplierName)
    setBrand(item.brand ?? '')
    setPrice(item.price != null ? String(item.price) : '')
    setExternalSku(item.externalSku ?? '')
  }

  const handleMerge = async () => {
    if (keepId == null) return
    const mergeIds = group.items.map((item) => item.id).filter((id) => id !== keepId)
    if (mergeIds.length === 0) {
      setError('Выберите позицию, которую нужно оставить')
      return
    }
    setMerging(true)
    setError(null)
    try {
      const parsedPrice = price.trim() === '' ? undefined : Number(price.replace(',', '.'))
      if (parsedPrice != null && Number.isNaN(parsedPrice)) {
        setError('Некорректная цена')
        setMerging(false)
        return
      }
      const payload = {
        organizationId,
        keepItemId: keepId,
        mergeItemIds: mergeIds,
        name: name.trim() || undefined,
        brand: brand.trim() || null,
        price: parsedPrice,
        externalSku: externalSku.trim() || null,
      }
      if (isMyPrices) {
        await mergeMyPricesDuplicates(payload)
      } else {
        await mergeCatalogDuplicates(payload)
      }
      await onMerged()
      setGroupIndex(0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось объединить дубликаты')
    } finally {
      setMerging(false)
    }
  }

  const handleNotDuplicates = () => {
    onNotDuplicates(group)
  }

  return (
    <article className="echat-panel market-catalog-duplicates" aria-label="Вероятные дубликаты">
      <div className="market-catalog-duplicates__header">
        <div>
          <h2 className="echat-panel__subtitle">Вероятные дубликаты</h2>
          <p className="echat-muted">
            Группа {safeIndex + 1} из {groups.length}: {reasonLabel(group.matchReason)}
            {group.matchKey ? ` «${group.matchKey}»` : ''}
          </p>
        </div>
        <button type="button" className="echat-btn echat-btn--ghost" onClick={onDismiss}>
          Скрыть
        </button>
      </div>

      <p className="market-hint">
        Выберите корректную позицию и объедините — остальные строки группы будут деактивированы. Если
        это разные товары, нажмите «Это не дубликаты».
      </p>

      <div className="market-catalog-duplicates__list" role="radiogroup" aria-label="Какую позицию оставить">
        {group.items.map((item) => {
          const selected = keepId === item.id
          return (
            <label
              key={item.id}
              className={`market-catalog-duplicates__item${
                selected ? ' market-catalog-duplicates__item--selected' : ''
              }`}
            >
              <input
                type="radio"
                name="catalog-duplicate-keep"
                checked={selected}
                onChange={() => handleSelectKeep(item)}
              />
              <span className="market-catalog-duplicates__item-body">
                <strong>{item.supplierName}</strong>
                <span className="echat-muted">{itemMetaLine(item)}</span>
                <span className="market-catalog-duplicates__item-uploaded echat-muted">
                  Загружено в прайс: {formatUploadedAt(item.updatedAt)}
                  {item.daysSinceUpdate != null && item.daysSinceUpdate > 0
                    ? ` · ${item.daysSinceUpdate} дн. назад`
                    : null}
                </span>
              </span>
            </label>
          )
        })}
      </div>

      <div className="market-catalog-edit__grid market-catalog-duplicates__edit">
        <label className="echat-field">
          <span className="echat-field__label">Наименование</span>
          <input
            className="echat-field__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Артикул</span>
          <input
            className="echat-field__input"
            value={externalSku}
            onChange={(event) => setExternalSku(event.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Бренд</span>
          <input
            className="echat-field__input"
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Цена</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
        </label>
      </div>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="market-catalog-duplicates__actions">
        <button
          type="button"
          className="echat-btn"
          disabled={merging || keepId == null}
          onClick={() => void handleMerge()}
        >
          {merging ? 'Объединение…' : 'Объединить в выбранную'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost"
          disabled={merging}
          onClick={handleNotDuplicates}
        >
          Это не дубликаты
        </button>
        {groups.length > 1 ? (
          <div className="market-catalog-duplicates__nav">
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={merging || safeIndex <= 0}
              onClick={() => setGroupIndex((current) => current - 1)}
            >
              Предыдущая группа
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={merging || safeIndex >= groups.length - 1}
              onClick={() => setGroupIndex((current) => current + 1)}
            >
              Следующая группа
            </button>
          </div>
        ) : null}
      </div>
    </article>
  )
}
